<script setup lang="ts">
import type { OpenFormPropertyPayload, PropertyContactSectionProps } from '.';

import { PropertyContactFormModal } from '~/components/Property/PropertyContactFormModal';
import { Skeleton } from '~/components/ui/skeleton';

import { useI18n } from 'vue-i18n';
import { getAgentFirstName, getAgentName } from '~~/utils';
import messages from './PropertyContactSection.locale.json';

const { t } = useI18n({
	useScope: 'local',
	messages
});

const props = withDefaults(defineProps<PropertyContactSectionProps>(), {
	companyLogo: '/images/fallbacks/company-avatar.webp'
});

const emit = defineEmits<{
	(e: 'form_property_clicked', payload: OpenFormPropertyPayload): void;
}>();

const isContactModalOpen = ref(false);

const openContactModal = () => {
	isContactModalOpen.value = true;
};

const agentFirstName = computed(() => getAgentFirstName(props.agent));
const agentName = computed(() => getAgentName(props.agent));

const ctaText = computed(() => `${t('speakWith')} ${agentFirstName.value}`);

function handleOpenContactModal() {
	openContactModal();

	emit('form_property_clicked', {
		ctaText: ctaText.value,
		companyName: props.companyName,
		agentName: props.agent.name,
		askingPrice: props.askingPrice
	});
}

const getImageSrc = useImage();

const brokerSrc = computed(() =>
	getImageSrc(
		props.agent?.profilePicture.croppedUrl || '',
		{
			format: 'webp',
			width: 460
		},
		{
			provider: 'AWSServerlessImageHandler'
		}
	)
);
</script>

<template>
	<div
		v-if="isLoading"
		class="flex h-[240px] w-full items-center gap-4 bg-white-dark-2 p-5 md:p-8"
	>
		<Skeleton :is-active="true" variant="whiteHighlight" class="size-[180px]" />

		<div class="space-y-4">
			<Skeleton
				:is-active="true"
				variant="whiteHighlight"
				class="h-6 w-[120px]"
			/>
			<Skeleton
				:is-active="true"
				variant="whiteHighlight"
				class="h-[18px] w-[140px]"
			/>

			<div class="space-y-2">
				<Skeleton
					v-for="index in 3"
					:key="`property-contact-section-skeleton-line-${index}`"
					:is-active="true"
					variant="whiteHighlight"
					class="h-2 w-full last:w-3/5"
				/>
			</div>

			<Skeleton
				:is-active="true"
				variant="whiteHighlight"
				class="h-[50px] w-[200px]"
			/>
		</div>
	</div>

	<div v-else class="flex max-w-full flex-col gap-4">
		<h2
			class="font-matter-sq text-[28px] font-normal leading-[42px] text-primary"
		>
			{{ t('speakWith') }} {{ agentFirstName }}
		</h2>
		<div
			class="flex w-full flex-col justify-center gap-3 bg-secondary p-5 md:flex-row md:gap-5 md:p-8"
		>
			<div class="flex items-end">
				<BrokerAvatar
					:broker-src="brokerSrc || '/images/fallbacks/agent-avatar.webp'"
					:company-src="companyLogo"
					:show-company-avatar="true"
					avatar-shape="square"
					company-avatar-shape="circle"
					class="h-[240px] w-full md:size-[152px]"
					sizes="containedRight"
					:broker-name="agentName"
					:company-name="companyName"
				/>
			</div>
			<div class="flex flex-col justify-between gap-5 md:gap-3">
				<div class="flex flex-col gap-3 md:gap-2">
					<div class="flex flex-col gap-[0.12rem] md:gap-0">
						<h3
							class="font-matter-sq text-[20px] font-normal leading-[30px] tracking-[-0.025rem] text-primary"
						>
							{{ agentName }}
						</h3>
						<span class="font-sans text-xs font-normal text-primary-light-3">
							{{ t('brokerAt') }} {{ companyName }}
						</span>
					</div>
					<span class="font-sans text-sm font-light italic text-primary">
						"{{ t('greeting', { brokerName: agentFirstName }) }}"
					</span>
				</div>
				<div class="flex w-full items-center justify-start">
					<Button
						variant="primary-dark"
						class="h-[12] w-fit justify-start border border-white-dark-3 px-4 py-3"
						@click="handleOpenContactModal"
					>
						<IconMaterialSymbolsLightChatBubbleOutline class="size-5" />
						<span class="font-sans text-base font-normal text-primary">
							{{ ctaText }}
						</span>
					</Button>
					<PropertyContactFormModal
						v-model="isContactModalOpen"
						:broker="agent"
						:company-name="companyName"
						:property-commercial-id="commercialId"
						:property-region="region"
						:property-type-name="propertyType?.name"
						:property-id="propertyId"
						:asking-price="askingPrice"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
